import { createServerFn } from "@tanstack/react-start";
import { createAuthenticatedApiConfig, throwApiError } from "@/lib/utils.server";
import { isForbiddenError } from "@/lib/utils";
import {
  OverriddenSettingsListResponse,
  SettingListResponse,
  SettingOverrides,
  SettingType,
} from "@/lib/mma/types.gen";
import {
  getSettingTypes,
  getSettings,
  updateSettings,
  getSettingOverrides,
  removeSettingOverrides,
  getOverriddenSettings,
} from "@/lib/mma/sdk.gen";

export type GroupedSettingType = {
  groupId: number;
  settings: (SettingType & { name: string })[];
  displayName?: string;
  displayOrder?: number;
};

export type GroupedSettingTypeResponse = {
  data: GroupedSettingType[];
  meta: {
    groups: {
      [key: string]: {
        displayName?: string;
        displayOrder?: number;
      };
    };
  };
};

export const fetchSettingTypesFn = createServerFn({ method: "GET" }).handler(
  (async (): Promise<GroupedSettingTypeResponse> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getSettingTypes(config);
    if (!response.data) {
      return await throwApiError(response, "Failed to get setting types", response.response?.status);
    }

    //Group setting types by groupId for easier access.
    let groupedSettingTypes = response?.data?.data
      ? Object.entries(response.data.data).reduce<
          {
            groupId: number;
            settings: (SettingType & { name: string })[];
            displayName?: string;
            displayOrder?: number;
          }[]
        >((acc, [name, setting]) => {
          const groupId = setting.groupId;
          let group = acc.find((g) => g.groupId === groupId);

          if (!group) {
            group = {
              groupId,
              settings: [],
              ...response.data.meta?.groups?.[groupId],
            };
            acc.push(group);
          }

          // Attach the original key as "name" property
          group.settings.push({ ...setting, name });

          return acc;
        }, [])
      : [];

    // Sort settings within each group
    groupedSettingTypes = groupedSettingTypes.filter((group) => {
      if (group.groupId === 21) return false; //TODO: Remove this once GDPR settings are removed from the payload.
      if (group.settings.length === 0) return false;
      return true;
    });
    groupedSettingTypes.forEach((group) => {
      group.settings.sort((a, b) => a.orderWithinGroup - b.orderWithinGroup);
    });

    groupedSettingTypes.push({
      displayName: "GDPR",
      groupId: 1000,
      displayOrder: 1000,
      settings: [
        {
          domain: "ORGANISATION",
          name: "clear_gdpr",
          displayName: "Clear accepted GDPR terms",
          description:
            "Clear accepted terms flag for users. This means that the next time the user login the user will be prompted to accept the terms and condotions again.",
          groupId: 1000,
          orderWithinGroup: 1,
          visibleOnSystem: true,
          validation: { type: "textbox", textbox: null, choices: null },
        },
      ],
    });
    return {
      data: groupedSettingTypes as unknown as GroupedSettingTypeResponse["data"],
      meta: response?.data?.meta as unknown as GroupedSettingTypeResponse["meta"],
    };
  }) as any
);

export const fetchSystemSettingsFn = createServerFn({ method: "GET" }).handler(
  (async (): Promise<SettingListResponse> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getSettings(config);
    if (!response.data) {
      return await throwApiError(response, "Failed to get system settings", response.response?.status);
    }
    return (response.data?.data ?? { data: {} }) as unknown as SettingListResponse; //Casting to unknown to avoid type errors generated by a type missmatch between OpenAPI and Tanstack
  }) as any
);

export const settingTypesQueryOptions = {
  queryKey: ["settingTypes"],
  queryFn: async () => fetchSettingTypesFn(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
};

export const systemSettingsQueryOptions = {
  queryKey: ["systemSettings"],
  queryFn: async () => fetchSystemSettingsFn(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
};

export const updateSystemSettingsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { payload: any }) => data)
  .handler(
    async ({ data }: { data: { payload: any } | undefined }): Promise<{ systemSettings: SettingListResponse }> => {
      const config = await createAuthenticatedApiConfig();
      const response = await updateSettings({ ...config, body: data?.payload });
      if (!response.data) {
        return await throwApiError(response, "Failed to update system settings", response.response?.status);
      }
      return { systemSettings: response.data?.data || {} };
    }
  );

export const minuteCounterOverridesQuery = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ minuteCounterOverrides: SettingOverrides }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getSettingOverrides({ ...config, path: { name: "SYS_ENABLE_MINUTE_COUNTER" } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get minute counter overrides", response.response?.status);
    }
    return { minuteCounterOverrides: response.data || {} };
  }
);

export const minuteCounterOverridesQueryOptions = {
  queryKey: ["minuteCounterOverrides"],
  queryFn: async () => minuteCounterOverridesQuery(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
};

export const getServiceTypeFn = createServerFn({ method: "GET" }).handler(async (): Promise<SettingOverrides> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getSettingOverrides({ ...config, path: { name: "SYS_SERVICE_TYPE" } });
  if (!response.data) {
    return await throwApiError(response, "Failed to get service type overrides", response.response?.status);
  }
  return response.data || {};
});

export const getDivergenceSearchMaxDaysOptions = {
  queryKey: ["divergenceSearchMaxDaysOptions"],
  queryFn: async () => getDivergenceSearchMaxDaysFn(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
};

export const getDivergenceSearchMaxDaysFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<SettingOverrides> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getSettingOverrides({ ...config, path: { name: "SYS_DIVERGENCE_SEARCH_MAX_DAYS" } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get divergence search max days", response.response?.status);
    }
    return response.data || {};
  }
);

export const serviceTypeQueryOptions = {
  queryKey: ["serviceType"],
  queryFn: async () => getServiceTypeFn(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
};

export const getDefaultClientTypesFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<SettingOverrides> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getSettingOverrides({ ...config, path: { name: "SYS_DEFAULT_CLIENT_TYPES" } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get default client types", response.response?.status);
    }
    return response.data || [];
  }
);

export const defaultClientTypesQueryOptions = {
  queryKey: ["defaultClientTypes"],
  queryFn: async () => getDefaultClientTypesFn(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
};

export const getSettingOverridesFn = createServerFn({ method: "GET" })
  .inputValidator((data: { setting: string }) => data)
  .handler(async ({ data }: { data: { setting: string } }): Promise<SettingOverrides> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getSettingOverrides({ ...config, path: { name: data.setting } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get setting overrides", response.response?.status);
    }
    return response.data || {};
  });

export const getSettingOverridesQueryOptions = (setting: string) => {
  return {
    queryKey: ["setting-overrides", setting],
    queryFn: async () => getSettingOverridesFn({ data: { setting } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
    refetchOnReconnect: false,
    enabled: setting !== null && setting !== undefined && setting !== "",
    retry: false,
  };
};

export const clearOverridesFn = createServerFn({ method: "POST" })
  .inputValidator((data: { setting: string }) => data)
  .handler(async ({ data }: { data: { setting: string } }): Promise<{ clearOverrides: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await removeSettingOverrides({ ...config, path: { name: data.setting } });
    if (!response.data) {
      return await throwApiError(response, "Failed to clear setting overrides", response.response?.status);
    }
    return { clearOverrides: true };
  });

export const getOverriddenSettingsFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<OverriddenSettingsListResponse> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOverriddenSettings({ ...config });
    if (!response.data) {
      return await throwApiError(response, "Failed to get overridden settings", response.response?.status);
    }
    return response.data || [];
  }
);

export const systemOverwrittenSettingsQueryOptions = {
  queryKey: ["systemOverwrittenSettings"],
  queryFn: async () => getOverriddenSettingsFn(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
};
